import { cloneElement, isValidElement, type ReactElement } from 'react';
import { NavLink } from 'react-router-dom';

import { nav, type NavItem } from '@/config/nav';
import type { Role } from '@/types';

import styles from './sidebar-nav.module.scss';

type Group = { section?: string; items: NavItem[] };

function groupBySection(items: NavItem[]): Group[] {
  const groups: Group[] = [];
  for (const it of items) {
    const last = groups[groups.length - 1];
    if (last && last.section === it.section) last.items.push(it);
    else groups.push({ section: it.section, items: [it] });
  }
  return groups;
}

function visible(it: NavItem, roles: Role[] | undefined): boolean {
  return !it.roles || it.roles.some((r) => roles?.includes(r));
}

export function SidebarNav({ roles }: { roles: Role[] | undefined }) {
  const groups = groupBySection(nav.filter((it) => visible(it, roles)));

  return (
    <nav className={styles.nav}>
      {groups.map((group, i) => (
        <div className={styles.group} key={group.section ?? `group-${i}`}>
          {group.section ? <p className={styles.groupTitle}>{group.section}</p> : null}
          {group.items.map((it) => (
            <NavLink
              key={it.key}
              to={it.to}
              end={it.to === '/'}
              className={({ isActive }) =>
                [styles.item, isActive ? styles.itemActive : undefined].filter(Boolean).join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  <span className={styles.icon}>
                    {isActive && isValidElement(it.icon)
                      ? cloneElement(it.icon as ReactElement<{ weight?: string }>, {
                          weight: 'fill',
                        })
                      : it.icon}
                  </span>
                  <span className={styles.label}>{it.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}
